import { useCallback, useEffect, useRef } from "react";

type UseBarcodeScannerOptions = {
  onScan: (barcode: string) => void;
  minLength?: number;
  maxLength?: number;
  timeout?: number;
};

/** ไม่ดักบาร์โค้ดเมื่อเหตุการณ์มาจาก modal/dialog — ไม่ให้ปนกับ input ที่ซ้อนบน POS */
function isEventFromModal(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return Boolean(
    target.closest(
      '[role="dialog"], [role="alertdialog"], [data-radix-dialog-content], [data-radix-alert-dialog-content]',
    ),
  );
}

/**
 * Hook for detecting barcode scanner input
 * Barcode scanners typically send characters very quickly followed by Enter
 * This hook distinguishes between manual typing and barcode scanning
 */
export function useBarcodeScanner({
  onScan,
  minLength = 3,
  maxLength = 50,
  timeout = 100, // ms between characters to consider it a scan
}: UseBarcodeScannerOptions) {
  const inputRef = useRef<string>("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastCharTimeRef = useRef<number>(0);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isEventFromModal(e.target)) {
        return;
      }

      // Ignore modifier keys
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      const now = Date.now();
      const timeSinceLastChar = now - lastCharTimeRef.current;

      // If Enter is pressed and we have input, check if it's a barcode scan
      if (e.key === "Enter" && inputRef.current.length >= minLength) {
        e.preventDefault();
        e.stopPropagation();

        // If characters were entered very quickly (within timeout), it's likely a scan
        if (timeSinceLastChar < timeout * 2) {
          const barcode = inputRef.current.trim();
          if (barcode.length >= minLength && barcode.length <= maxLength) {
            onScan(barcode);
          }
        }

        inputRef.current = "";
        lastCharTimeRef.current = 0;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      // Handle regular character input
      if (e.key.length === 1) {
        // Clear timer if it exists
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        // If too much time passed since last char, reset (likely manual typing)
        if (timeSinceLastChar > timeout * 3) {
          inputRef.current = "";
        }

        inputRef.current += e.key;
        lastCharTimeRef.current = now;

        // Set timer to clear input if no more characters come
        timerRef.current = setTimeout(() => {
          inputRef.current = "";
        }, timeout * 2);
      }

      // Handle backspace
      if (e.key === "Backspace") {
        inputRef.current = inputRef.current.slice(0, -1);
        lastCharTimeRef.current = now;
      }
    },
    [onScan, minLength, maxLength, timeout],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (isEventFromModal(e.target)) {
        return;
      }

      const pastedText = e.clipboardData?.getData("text")?.trim();
      if (
        pastedText &&
        pastedText.length >= minLength &&
        pastedText.length <= maxLength
      ) {
        e.preventDefault();
        e.stopPropagation();
        onScan(pastedText);
        inputRef.current = "";
      }
    },
    [onScan, minLength, maxLength],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("paste", handlePaste);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [handleKeyDown, handlePaste]);

  return {
    clearInput: useCallback(() => {
      inputRef.current = "";
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }, []),
  };
}
